export class CreateLeagueModal {
  private modal: HTMLElement | null = null;

  async show(): Promise<{
    name: string;
    description: string;
    format: "swiss" | "roundrobin" | "individual_swiss" | "individual_roundrobin";
  } | null> {
    return new Promise((resolve) => {
      this.modal = document.createElement("div");
      this.modal.className =
        "fixed inset-0 bg-slate-900/80 backdrop-blur-sm overflow-y-auto h-full w-full flex items-center justify-center z-50 p-4";
      this.modal.innerHTML = `
        <div class="relative p-6 w-full max-w-md bg-white dark:bg-surface-dark daylight:bg-day-raised rounded-2xl shadow-xl daylight:shadow-day-modal border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10">
            <div class="mb-6">
                <h3 class="text-2xl font-bold text-slate-900 dark:text-white daylight:text-day-ink">Create New League</h3>
                <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft text-sm mt-1">Set up a new league for your players.</p>
            </div>
            <form id="createLeagueForm" class="space-y-5">
                <div>
                    <label class="block text-sm font-bold text-slate-900 dark:text-white mb-2">League Name</label>
                    <input type="text" id="leagueName" required
                        class="input-field w-full"
                        placeholder="e.g. Summer Championship 2024">
                </div>
                
                <div>
                    <label class="block text-sm font-bold text-slate-900 dark:text-white mb-2">Description</label>
                    <textarea id="leagueDescription" rows="3"
                        class="input-field w-full py-2"
                        placeholder="Optional description..."></textarea>
                </div>

                <div>
                    <label class="block text-sm font-bold text-slate-900 dark:text-white mb-2">League Type</label>
                    <div class="flex bg-slate-100 dark:bg-slate-800 daylight:bg-day-sunken p-1 rounded-lg" id="typeToggle">
                        <button type="button" class="flex-1 py-2 px-4 rounded-md text-sm font-bold transition-all bg-white dark:bg-slate-700 daylight:bg-day-raised shadow-sm daylight:shadow-day-sm text-primary dark:text-primary daylight:text-day-ink ring-1 ring-black/5 dark:ring-white/10" data-value="pairs">Pairs</button>
                        <button type="button" class="flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted hover:text-slate-700 dark:hover:text-slate-200 daylight:hover:text-day-ink" data-value="individual">Individual</button>
                    </div>
                </div>

                    <div>
                    <div id="system-toggle-wrapper" class="transition-opacity">
                        <label class="block text-sm font-bold text-slate-900 dark:text-white mb-2">Scoring System</label>
                        <div class="flex bg-slate-100 dark:bg-slate-800 daylight:bg-day-sunken p-1 rounded-lg" id="systemToggle">
                            <button type="button" class="flex-1 py-2 px-4 rounded-md text-sm font-bold transition-all bg-white dark:bg-slate-700 daylight:bg-day-raised shadow-sm daylight:shadow-day-sm text-primary dark:text-primary daylight:text-day-ink ring-1 ring-black/5 dark:ring-white/10" data-value="swiss">Swiss</button>
                            <button type="button" class="flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted hover:text-slate-700 dark:hover:text-slate-200 daylight:hover:text-day-ink" data-value="roundrobin">Round Robin</button>
                        </div>
                    </div>
                    <p id="system-helper-text" class="hidden text-xs text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted mt-2">Individual leagues support both Swiss and Round Robin formats.</p>
                </div>

                <div class="flex gap-3 pt-4">
                    <button type="button" id="cancelBtn"
                        class="btn-secondary flex-1">
                        Cancel
                    </button>
                    <button type="submit"
                        class="btn-primary flex-1">
                        <span class="material-symbols-outlined">add_circle</span>
                        <span>Create League</span>
                    </button>
                </div>
            </form>
        </div>
      `;

      document.body.appendChild(this.modal);

      const form = this.modal.querySelector("#createLeagueForm") as HTMLFormElement;
      const cancelBtn = this.modal.querySelector("#cancelBtn") as HTMLButtonElement;
      const nameInput = this.modal.querySelector("#leagueName") as HTMLInputElement;
      const descInput = this.modal.querySelector("#leagueDescription") as HTMLTextAreaElement;
      
      const systemToggleWrapper = this.modal.querySelector("#system-toggle-wrapper") as HTMLElement;
      const systemHelperText = this.modal.querySelector("#system-helper-text") as HTMLElement;

      let selectedType: "pairs" | "individual" = "pairs";
      let selectedSystem: "swiss" | "roundrobin" = "swiss";

      const updateToggle = (containerId: string, value: string) => {
        const container = this.modal!.querySelector(`#${containerId}`);
        const buttons = container!.querySelectorAll('button');
        buttons.forEach(btn => {
            if (btn.dataset.value === value) {
                btn.className = "flex-1 py-2 px-4 rounded-md text-sm font-bold transition-all bg-white dark:bg-slate-700 daylight:bg-day-raised shadow-sm daylight:shadow-day-sm text-primary dark:text-primary daylight:text-day-ink ring-1 ring-black/5 dark:ring-white/10";
            } else {
                btn.className = "flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted hover:text-slate-700 dark:hover:text-slate-200 daylight:hover:text-day-ink";
            }
        });
      };

      this.modal.querySelector("#typeToggle")!.addEventListener("click", (e) => {
        const btn = (e.target as HTMLElement).closest("button");
        if (btn) {
            selectedType = btn.dataset.value as "pairs" | "individual";
            updateToggle("typeToggle", selectedType);

            if (selectedType === 'individual') {
                // Allow both Swiss and Round Robin for individual leagues
                systemToggleWrapper.style.pointerEvents = 'auto';
                systemToggleWrapper.style.opacity = '1';
                systemHelperText.classList.remove('hidden');
            } else {
                systemToggleWrapper.style.pointerEvents = 'auto';
                systemToggleWrapper.style.opacity = '1';
                systemHelperText.classList.add('hidden');
            }
        }
      });

      this.modal.querySelector("#systemToggle")!.addEventListener("click", (e) => {
        const btn = (e.target as HTMLElement).closest("button");
        if (btn) {
            selectedSystem = btn.dataset.value as "swiss" | "roundrobin";
            updateToggle("systemToggle", selectedSystem);
        }
      });

      nameInput.focus();

      const cleanup = () => {
        if (this.modal) {
          document.body.removeChild(this.modal);
          this.modal = null;
        }
      };

      form.addEventListener("submit", (e) => {
        e.preventDefault();
        
        let format: "swiss" | "roundrobin" | "individual_swiss" | "individual_roundrobin";
        if (selectedType === "pairs") {
            format = selectedSystem === "swiss" ? "swiss" : "roundrobin";
        } else {
            format = selectedSystem === "swiss" ? "individual_swiss" : "individual_roundrobin";
        }

        const data = {
          name: nameInput.value,
          description: descInput.value,
          format,
        };
        cleanup();
        resolve(data);
      });

      cancelBtn.addEventListener("click", () => {
        cleanup();
        resolve(null);
      });

      this.modal.addEventListener("click", (e) => {
        if (e.target === this.modal) {
          cleanup();
          resolve(null);
        }
      });
    });
  }
}